import { useState, useEffect, useCallback } from 'react';

// A setting is "enabled" purely from its VALUE (the Status column has been
// retired — status is only a derived mirror kept for backend compatibility):
//   - boolean value true / 'true'        → enabled
//   - non-empty string / number / json   → enabled (empty, 'false', {}, [] → off)
// This is the single source of truth every consumer uses to decide on/off.
export function valueIsEnabled(value) {
  if (value === true || value === 'true') return true;
  if (value === false || value === null || value === undefined) return false;
  const s = String(value).trim();
  if (s === '' || s === 'false' || s === '{}' || s === '[]') return false;
  return true;
}

// Non-hook helper for code paths that already hold the app_setting array (e.g.
// forms reading localStorage directly). Judges a module's enablement from its
// value, exactly like the hooks — so nothing depends on the retired status field.
export function settingEnabled(settings, moduleName, defaultEnabled = false) {
  try {
    const key = String(moduleName).trim().toLowerCase().replace(/\s+/g, '');
    const found = (settings || []).find(
      (s) => s?.module?.trim().toLowerCase().replace(/\s+/g, '') === key
    );
    return found ? valueIsEnabled(found.value) : defaultEnabled;
  } catch {
    return defaultEnabled;
  }
}

// Returns the string `value` field of an app setting module — but ONLY when the
// setting is enabled (value is meaningful). A value-less setting is treated as
// inactive, so consumers get '' and fall back to their defaults.
export function useAppSettingValue(moduleName) {
  const readValue = useCallback(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('app_setting') || '[]');
      const key = moduleName.trim().toLowerCase().replace(/\s+/g, '');
      const found = settings.find(
        (s) => s?.module?.trim().toLowerCase().replace(/\s+/g, '') === key
      );
      if (!found || !valueIsEnabled(found.value)) return '';
      return found.value || '';
    } catch {
      return '';
    }
  }, [moduleName]);

  const [value, setValue] = useState(() => readValue());

  useEffect(() => { setValue(readValue()); }, [moduleName, readValue]);

  useEffect(() => {
    const handler = () => setValue(readValue());
    window.addEventListener('app_setting_updated', handler);
    return () => window.removeEventListener('app_setting_updated', handler);
  }, [readValue]);

  return value;
}

// Returns a function that maps original material type name → alias (falls back to original)
export function useMaterialTypeAlias() {
  const raw = useAppSettingValue('Material Type Alias');

  return (originalName) => {
    if (!raw || !originalName) return originalName;
    try {
      const map = JSON.parse(raw);
      const alias = map[originalName];
      return alias && alias.trim() ? alias.trim() : originalName;
    } catch {
      return originalName;
    }
  };
}

// Returns a function that maps an original sub-operation label → alias
// (falls back to the original label). Configured in Application Settings.
export function useSubOperationAlias() {
  const raw = useAppSettingValue('Sub-operation Alias');

  return (originalName) => {
    if (!raw || !originalName) return originalName;
    try {
      const map = JSON.parse(raw);
      const alias = map[originalName];
      return alias && alias.trim() ? alias.trim() : originalName;
    } catch {
      return originalName;
    }
  };
}

// Returns the configured display name for "MBMR" across the whole app
// (falls back to "MBMR" when no alias is set). Unlike the per-key aliases
// above, MBMR is a single global term, so this returns a plain string
// rather than a lookup function. Configured in Application Settings.
export function useMbmrAlias() {
  const raw = useAppSettingValue('MBMR Alias');
  return raw && raw.trim() ? raw.trim() : 'MBMR';
}

// Returns a function that maps an original operation type name → alias
// (falls back to the original name). Configured in Application Settings.
export function useOperationTypeAlias() {
  const raw = useAppSettingValue('Operation Type Alias');

  return (originalName) => {
    if (!raw || !originalName) return originalName;
    try {
      const map = JSON.parse(raw);
      const alias = map[originalName];
      return alias && alias.trim() ? alias.trim() : originalName;
    } catch {
      return originalName;
    }
  };
}

export default function useAppSetting(moduleName, defaultEnabled = false) {
  const readValue = useCallback(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('app_setting') || '[]');
      const key = moduleName.trim().toLowerCase().replace(/\s+/g, '');
      const found = settings.find(
        (s) => s?.module?.trim().toLowerCase().replace(/\s+/g, '') === key
      );
      return found ? valueIsEnabled(found.value) : defaultEnabled;
    } catch {
      return defaultEnabled;
    }
  }, [moduleName, defaultEnabled]);

  const [enabled, setEnabled] = useState(() => readValue());

  useEffect(() => {
    // Re-read immediately when moduleName changes
    setEnabled(readValue());
  }, [moduleName, readValue]);

  useEffect(() => {
    const handler = () => setEnabled(readValue());
    window.addEventListener('app_setting_updated', handler);
    return () => window.removeEventListener('app_setting_updated', handler);
  }, [readValue]);

  return enabled;
}
